import type { Index } from "meilisearch"
import type { WatchSource } from "vue"
import { tryOnScopeDispose } from "@vueuse/core"
import { ErrorStatusCode, MeiliSearch, MeiliSearchApiError } from "meilisearch"

/**
 * Whether Meilisearch rejected the tenant token served by `/api/auth/me` (expired or invalid), in
 * which case a fresh one must be fetched (see `useMeilisearchTokenRefresh`).
 *
 * NOTE: matched on the error code, as the message differs: an expired tenant token answers
 * "Tenant token expired. Was valid up to ..." instead of "The provided API key is invalid.".
 */
export function isMeilisearchAuthError(error: unknown): boolean {
  return (
    error instanceof MeiliSearchApiError && error.cause?.code === ErrorStatusCode.INVALID_API_KEY
  )
}

/**
 * Recovers searches rejected for an expired (or otherwise invalid) tenant token: refetches the
 * `["user", "me"]` query serving the token, then runs the retry of the rejected search once the
 * index built from the fresh token replaces the current one.
 *
 * NOTE: the same search `key` refreshes the token at most once in a row, so a fresh token rejected
 * too gives up instead of looping. Keys follow user inputs, so a later expiry still refreshes.
 *
 * @param index Source of the index built from the current token, e.g. `useMeilisearchIndex()`
 * @returns `onSearchError(error, key, retry)`, to call with any error of the search `key`
 */
export function useMeilisearchTokenRefresh(index: WatchSource<Index | undefined>) {
  const queryClient = useQueryClient()

  let refreshedKey: string | undefined
  let pendingRetry: (() => unknown) | undefined

  watch(index, () => {
    const retry = pendingRetry
    pendingRetry = undefined
    retry?.()
  })

  async function onSearchError(error: unknown, key: string, retry: () => unknown) {
    if (!isMeilisearchAuthError(error) || key === refreshedKey) return

    refreshedKey = key
    pendingRetry = retry
    await queryClient.invalidateQueries({ queryKey: ["user", "me"] })
  }

  return { onSearchError }
}

/**
 * Keeps only the latest search of a search-as-you-type input alive: each `start()` aborts the
 * previous search and returns the signal of the new one, to pass to `index.search(q, opts, {
 * signal })`. Aborting the request matters server-side: a search still queued in Meilisearch never
 * runs once its client disconnects.
 *
 * NOTE: `signal.aborted` also tells a superseded search apart once awaited, including the paths
 * that send no request, as long as every search calls `start()`. A superseded search must neither
 * report its error (the abort rejects the request) nor write results: the newer one owns them.
 *
 * @returns `start()` to call at the beginning of every search, and `abort()` for the in-flight one
 *   (also called when the calling scope is disposed)
 */
export function useLatestSearch() {
  let controller: AbortController | undefined

  function abort() {
    controller?.abort()
  }

  function start(): AbortSignal {
    abort()
    controller = new AbortController()

    return controller.signal
  }

  tryOnScopeDispose(abort)

  return { start, abort }
}

export function useMeilisearch(index: string, apiKey?: string | undefined) {
  const config = useRuntimeConfig()
  const client = new MeiliSearch({
    host: config.public.meilisearch.url,
    apiKey: apiKey || undefined,
  })
  return client.index(index)
}

/**
 * Returns a reactive Meilisearch Index for global search.
 * Automatically refetches user info (including meilisearch credentials) when the 24h cache expires.
 * Use this instead of the store getter for automatic cache management.
 *
 * @returns Computed ref to the Meilisearch Index, or undefined if credentials are not available
 */
export function useMeilisearchIndex() {
  const { data: user } = useGetUserInfos()

  return computed(() => {
    const meilisearch = user.value?.meilisearch
    if (!meilisearch?.topology_index || !meilisearch?.api_key) {
      return undefined
    }
    return useMeilisearch(meilisearch.topology_index, meilisearch.api_key)
  })
}

/**
 * Returns a reactive Meilisearch Index for DataStudio.
 * Automatically refetches user info (including meilisearch credentials) when the 24h cache expires.
 *
 * @returns Computed ref to the Meilisearch Index, or undefined if credentials are not available or
 *   the user lacks `read:datastudio` (the tenant token then does not grant access to the index)
 */
export function useDatastudioMeilisearchIndex() {
  const { data: user } = useGetUserInfos()

  return computed(() => {
    const meilisearch = user.value?.meilisearch
    if (
      !meilisearch?.datastudio_index ||
      !meilisearch?.api_key ||
      !hasScope(Scope.enum["read:datastudio"])
    ) {
      return undefined
    }
    return useMeilisearch(meilisearch.datastudio_index, meilisearch.api_key)
  })
}
